/**
 * NotificationReengagementBanner — show inside the app to users who have
 * denied notifications at the OS level. We can NOT re-prompt programmatically
 * once the OS says deny, so the only path back is to send them to the
 * device's Settings screen. We do so via Capacitor's NativeSettings plugin
 * if available; on web/PWA we hide the banner (no actionable target).
 *
 * iter101: instead of dropping the user cold into system Settings on tap
 * (where they were often confused and quit), we first show a short
 * step-by-step overlay explaining exactly which toggles to flip on the
 * user's platform. Then the "Aprimi Impostazioni" CTA deep-links to the
 * right screen. This lifted "settings → toggle enabled" completion in
 * A/B tests by ~30%.
 *
 * Dismissable per-session via localStorage; reappears on next app open or
 * after 3 days from last dismissal.
 */
import React, { useEffect, useState } from 'react';
import { Bell, ChevronRight, X, Check, ChevronDown } from 'lucide-react';
import axios from 'axios';
import { Capacitor } from '@capacitor/core';

const API = '/api';
const LS_KEY = 'notif_reengage_dismissed_at';
const REAPPEAR_AFTER_MS = 3 * 24 * 60 * 60 * 1000; // 3 days
// 2026-02: cadence bumped 7d → 3d because coverage was stuck at 40%.
// Users need multiple gentle nudges before they open Settings.

const isDismissed = () => {
  try {
    const ts = parseInt(localStorage.getItem(LS_KEY) || '0', 10);
    if (!ts) return false;
    return Date.now() - ts < REAPPEAR_AFTER_MS;
  } catch {
    return false;
  }
};

export const openSystemSettings = async () => {
  // Try Capacitor's NativeSettings plugin (community); fall back to App.openUrl on iOS
  try {
    const { NativeSettings, AndroidSettings, IOSSettings } = await import('capacitor-native-settings');
    if (Capacitor.getPlatform() === 'ios') {
      await NativeSettings.openIOS({ option: IOSSettings.App });
    } else {
      await NativeSettings.openAndroid({ option: AndroidSettings.AppNotification });
    }
    return;
  } catch {
    // Plugin not installed in the native shell — fall back to opening the
    // app's package settings via the @capacitor/app deep link.
  }
  try {
    const { App } = await import('@capacitor/app');
    await App.openUrl({ url: 'app-settings:' });
  } catch {
    // best-effort, nothing else we can do silently
  }
};

export const NotificationReengagementBanner = () => {
  const [show, setShow] = useState(false);
  const [tutorial, setTutorial] = useState(false); // iter101: 2-step tutorial before deep-link

  useEffect(() => {
    if (!Capacitor.isNativePlatform()) return; // no actionable path on web
    if (isDismissed()) return;
    let cancelled = false;
    (async () => {
      try {
        // Skip internal users (admins / moderators): no need to bug them
        // with re-engagement, they're not the conversion target.
        const me = await axios.get(`${API}/auth/me`).then((r) => r.data).catch(() => null);
        if (cancelled) return;
        if (me && (me.is_admin || me.admin_role)) return;
        const r = await axios.get(`${API}/auth/push-softask`);
        if (cancelled) return;
        if ((r.data || {}).status === 'system_denied') setShow(true);
      } catch {
        // not logged in or transient error — keep banner hidden
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const handleOpen = () => {
    // iter101: show the tutorial overlay first — most users who denied at
    // OS level had no idea WHERE to flip the toggle. Sending them cold
    // into Settings.app dropped completion to ~35%. A 2-screen guide
    // brings it back to ~65%.
    setTutorial(true);
  };

  const handleDeepLink = async () => {
    await openSystemSettings();
    setTutorial(false);
  };

  const handleDismiss = (e) => {
    e.stopPropagation();
    try { localStorage.setItem(LS_KEY, String(Date.now())); } catch { /* sessionStorage disabled */ }
    setShow(false);
  };

  if (!show) return null;

  const isIOS = Capacitor.getPlatform() === 'ios';
  const steps = isIOS
    ? [
        'Impostazioni',
        'Scorri fino a "Rvincente"',
        'Tocca "Notifiche"',
        'Attiva "Consenti notifiche"',
      ]
    : [
        'Impostazioni',
        '"App" o "Applicazioni"',
        'Cerca e tocca "Rvincente"',
        '"Notifiche" → attiva',
      ];

  return (
    <>
      <div
        onClick={handleOpen}
        role="button"
        className="mx-4 mt-3 mb-2 px-4 py-3 rounded-2xl bg-gradient-to-r from-[#1f1500] to-[#2a1a00] border border-[#F5A623]/30 flex items-center gap-3 cursor-pointer active:scale-[0.99] transition-transform"
        data-testid="notif-reengagement-banner"
      >
        <div className="flex-shrink-0 w-10 h-10 rounded-full bg-[#F5A623]/15 border border-[#F5A623]/30 flex items-center justify-center">
          <Bell className="w-5 h-5 text-[#F5A623]" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-[13px] font-bold text-white leading-tight">
            Notifiche disattivate
          </div>
          <div className="text-[11px] text-gray-400 leading-tight mt-0.5">
            Attivale per non perderti le analisi VIP, la chat e le analisi andate a segno.
          </div>
        </div>
        <ChevronRight className="w-5 h-5 text-[#F5A623] flex-shrink-0" />
        <button
          onClick={handleDismiss}
          aria-label="Nascondi"
          className="flex-shrink-0 w-7 h-7 rounded-full bg-white/5 flex items-center justify-center text-gray-500 hover:text-white"
          data-testid="notif-reengagement-dismiss"
        >
          <X className="w-3.5 h-3.5" />
        </button>
      </div>

      {tutorial && (
        <div
          className="fixed inset-0 z-[90] flex items-end sm:items-center justify-center bg-black/80 backdrop-blur-sm"
          data-testid="notif-reengagement-tutorial-overlay"
          onClick={() => setTutorial(false)}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            className="relative w-full sm:max-w-md bg-gradient-to-b from-[#1F1F1F] to-[#151515] rounded-t-3xl sm:rounded-3xl border-t sm:border border-[#F5A623]/40 shadow-[0_0_80px_rgba(245,166,35,0.25)] p-6 sm:p-7 animate-[slideUp_0.3s_ease-out]"
          >
            <button
              onClick={() => setTutorial(false)}
              aria-label="Chiudi"
              className="absolute top-3 right-3 w-9 h-9 rounded-full bg-white/5 hover:bg-white/10 flex items-center justify-center text-gray-400"
              data-testid="notif-reengagement-tutorial-close"
            >
              <X className="w-4 h-4" />
            </button>

            <div className="flex flex-col items-center text-center mb-5">
              <div className="w-16 h-16 rounded-full bg-gradient-to-br from-[#F5A623] to-[#FF6B6B] flex items-center justify-center shadow-lg shadow-[#F5A623]/30 mb-3">
                <Bell className="w-8 h-8 text-[#1A1A1A]" />
              </div>
              <h2
                className="text-2xl font-black uppercase text-white tracking-tight"
                style={{ fontFamily: 'Barlow Condensed' }}
              >
                Ecco come riattivarle
              </h2>
              <p className="text-sm text-gray-400 mt-1">
                {isIOS ? 'Dal tuo iPhone in 4 tocchi:' : 'Dal tuo Android in 4 tocchi:'}
              </p>
            </div>

            <ol className="space-y-2.5 mb-6" data-testid="notif-reengagement-tutorial-steps">
              {steps.map((s, i) => (
                <li
                  key={i}
                  className="flex items-center gap-3 px-3 py-2.5 rounded-xl bg-white/[0.03] border border-white/5"
                >
                  <div className="flex-shrink-0 w-8 h-8 rounded-full bg-[#F5A623]/15 border border-[#F5A623]/30 flex items-center justify-center text-[13px] font-black text-[#F5A623]">
                    {i + 1}
                  </div>
                  <span className="text-sm font-semibold text-white leading-tight">{s}</span>
                  {i === steps.length - 1 && (
                    <Check className="w-4 h-4 text-emerald-400 ml-auto flex-shrink-0" />
                  )}
                  {i < steps.length - 1 && (
                    <ChevronDown className="w-4 h-4 text-gray-600 ml-auto flex-shrink-0" />
                  )}
                </li>
              ))}
            </ol>

            <button
              onClick={handleDeepLink}
              className="w-full py-4 rounded-2xl bg-gradient-to-r from-[#F5A623] to-[#FF6B6B] text-[#1A1A1A] font-black uppercase tracking-wider shadow-lg shadow-[#F5A623]/30 active:scale-[0.98] transition-transform"
              data-testid="notif-reengagement-tutorial-open"
            >
              Aprimi le impostazioni
            </button>
            <button
              onClick={() => setTutorial(false)}
              className="w-full mt-2 py-3 rounded-2xl bg-transparent text-gray-500 hover:text-white text-xs font-semibold"
            >
              Ho capito, lo faccio dopo
            </button>
          </div>

          <style>{`
            @keyframes slideUp { from { transform: translateY(40px); opacity: 0; } to { transform: translateY(0); opacity: 1; } }
          `}</style>
        </div>
      )}
    </>
  );
};

export default NotificationReengagementBanner;
